import { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Plus, X } from "lucide-react";

const EMPTY_ON_HIT = { message: "", damage_dice: "", damage_type: "", apply_condition_slugs: [] };

// Shared attack-row editor (name/bonus/damage + optional on-hit effect with condition chips) —
// used by both MonsterBuilderPanel (building a reusable monster template) and BbebbegPanel
// (editing the live boss token's own attacks directly), so a raid Boss gets the exact same
// weapon/attack capability as any other NPC instead of a separate, lesser system.
export function AttackRowsEditor({ attacks, onChange, conditions, testIdPrefix }) {
  const [effectOpenIdx, setEffectOpenIdx] = useState(null);

  const addAttack = () => onChange([...attacks, { name: "", bonus: 0, damage: "", on_hit_effect: null }]);
  const updateAttack = (idx, key, value) => onChange(attacks.map((a, i) => (i === idx ? { ...a, [key]: value } : a)));
  const removeAttack = (idx) => onChange(attacks.filter((_, i) => i !== idx));
  const updateAttackEffect = (idx, patch) => {
    const attack = attacks[idx];
    updateAttack(idx, "on_hit_effect", { ...EMPTY_ON_HIT, ...(attack.on_hit_effect || {}), ...patch });
  };
  const toggleAttackCondition = (idx, slug) => {
    const current = attacks[idx].on_hit_effect?.apply_condition_slugs || [];
    const has = current.includes(slug);
    updateAttackEffect(idx, { apply_condition_slugs: has ? current.filter((s) => s !== slug) : [...current, slug] });
  };

  return (
    <div className="space-y-1.5">
      {attacks.map((a, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Input placeholder="Name" value={a.name} onChange={(e) => updateAttack(idx, "name", e.target.value)} className="bg-black/30 border-white/10 h-7 text-xs flex-1" />
            <Input type="number" placeholder="Bonus" value={a.bonus} onChange={(e) => updateAttack(idx, "bonus", parseInt(e.target.value, 10) || 0)} className="bg-black/30 border-white/10 h-7 text-xs w-14" />
            <Input placeholder="Damage" value={a.damage} onChange={(e) => updateAttack(idx, "damage", e.target.value)} className="bg-black/30 border-white/10 h-7 text-xs w-20" />
            <button
              type="button"
              data-testid={`${testIdPrefix}-attack-effect-toggle-${idx}`}
              onClick={() => setEffectOpenIdx(effectOpenIdx === idx ? null : idx)}
              title="On-hit effect (e.g. Poison)"
              className={`text-[10px] px-1.5 shrink-0 ${a.on_hit_effect ? "text-[#D4AF37]" : "text-[#f1e0c5]/30"}`}
            >
              FX
            </button>
            <button data-testid={`${testIdPrefix}-attack-remove-${idx}`} onClick={() => removeAttack(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          {effectOpenIdx === idx && (
            <div className="space-y-1.5 bg-black/20 border border-white/10 rounded-lg p-2 ml-1">
              <p className="text-[9px] uppercase text-[#D4AF37]/70">On-Hit Effect — parity with player weapon effects (e.g. Poison)</p>
              <Textarea
                data-testid={`${testIdPrefix}-attack-effect-message-${idx}`}
                value={a.on_hit_effect?.message || ""}
                onChange={(e) => updateAttackEffect(idx, { message: e.target.value })}
                placeholder="Text shown on a hit (optional)"
                className="bg-black/30 border-white/10 text-xs min-h-7"
              />
              <div className="flex gap-1.5">
                <Input
                  data-testid={`${testIdPrefix}-attack-effect-dice-${idx}`}
                  value={a.on_hit_effect?.damage_dice || ""}
                  onChange={(e) => updateAttackEffect(idx, { damage_dice: e.target.value })}
                  placeholder="Extra dmg e.g. 1d4"
                  className="bg-black/30 border-white/10 h-7 text-xs flex-1"
                />
                <Input
                  data-testid={`${testIdPrefix}-attack-effect-type-${idx}`}
                  value={a.on_hit_effect?.damage_type || ""}
                  onChange={(e) => updateAttackEffect(idx, { damage_type: e.target.value })}
                  placeholder="Type e.g. poison"
                  className="bg-black/30 border-white/10 h-7 text-xs flex-1"
                />
              </div>
              <div className="flex flex-wrap gap-1">
                {(conditions || []).map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    data-testid={`${testIdPrefix}-attack-effect-condition-${idx}-${c.slug}`}
                    onClick={() => toggleAttackCondition(idx, c.slug)}
                    className={`text-[9px] px-2 py-0.5 rounded-full border ${(a.on_hit_effect?.apply_condition_slugs || []).includes(c.slug) ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/10 text-[#f1e0c5]/50"}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
      <Button data-testid={`${testIdPrefix}-add-attack-button`} onClick={addAttack} size="sm" variant="secondary" className="rounded-full gap-1.5 h-7 text-xs"><Plus className="w-3 h-3" /> Add Attack</Button>
    </div>
  );
}
